import { useMemo } from 'react';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import {
  CompoundEntityRef,
  Entity,
  parseEntityRef,
  RELATION_OWNED_BY,
} from '@backstage/catalog-model';
import { Box, Chip, Typography } from '@material-ui/core';
import { useEntityNames } from '../catalog/useEntityNames';
import { useHomeSources } from './homeSources';

/**
 * A single component the signed-in user (via their team) owns, flattened from
 * a catalog Component entity into the columns the "Service Catalog" mockup
 * shows. The card is "Components you own" since DX-546 — a library or a
 * website is owned the same way, and the catalog calls them all components.
 *
 * Ownership is resolved server-side: the catalog `relations.ownedBy` filter is
 * matched against the caller's token ownership refs (DX-123).
 *
 * Four columns, each of which the card can fill. Health, last deploy and score
 * stood here too, as three columns of em-dashes on every row: a table whose
 * right half never says anything reads as broken rather than as unfinished, and
 * it said the same thing about a service the card knew nothing about as about
 * one it did. They come back with their sources — deployments under DX-80,
 * scorecards not before a writable database exists (DX-189, then DX-77) — and
 * each will then have to tell apart the several ways a value can be absent.
 */
type OwnedService = {
  name: string;
  namespace: string;
  kind: string;
  title: string;
  ownerRef?: string;
  /** Parsed form of `ownerRef`; undefined when absent or malformed. */
  owner?: CompoundEntityRef;
  /** What the Owner column shows, searches and sorts by. */
  ownerLabel: string;
  lifecycle?: string;
  type?: string;
};

const relationsOf = (entity: Entity, type: string): string[] =>
  (entity.relations ?? []).filter(r => r.type === type).map(r => r.targetRef);

/**
 * Parse an entity ref into its parts, or undefined if it is malformed.
 *
 * Catalog relations normally carry normalised full refs, but a hand-written
 * `spec.owner` can produce one that `parseEntityRef` rejects. Parsing once here
 * — rather than in the render path — keeps a bad ref from throwing inside React
 * and taking the whole table down with it.
 */
const parseRefSafely = (ref?: string): CompoundEntityRef | undefined => {
  if (!ref) return undefined;
  try {
    return parseEntityRef(ref);
  } catch {
    return undefined;
  }
};

const toOwnedService = (entity: Entity): OwnedService => {
  const ownerRef = relationsOf(entity, RELATION_OWNED_BY)[0];
  const owner = parseRefSafely(ownerRef);
  return {
    name: entity.metadata.name,
    namespace: entity.metadata.namespace ?? 'default',
    kind: entity.kind,
    title: entity.metadata.title ?? entity.metadata.name,
    ownerRef,
    // Only linkable when the ref parsed; the label still shows the raw value so
    // a malformed owner stays visible (and fixable) instead of rendering as "—".
    owner,
    ownerLabel: owner?.name ?? ownerRef ?? '—',
    lifecycle: (entity.spec?.lifecycle as string | undefined) ?? undefined,
    type: (entity.spec?.type as string | undefined) ?? undefined,
  };
};

const lifecycleColor = (lifecycle?: string): string => {
  switch (lifecycle) {
    case 'production':
      return '#2EA043';
    case 'experimental':
      return '#D29922';
    case 'deprecated':
      return '#F85149';
    default:
      // An unknown lifecycle is muted, not invisible — this was white, which
      // on the light surface meant the dot simply vanished.
      return 'var(--ui-text-muted)';
  }
};

const placeholder = (
  <Typography variant="body2" color="textSecondary" component="span">
    —
  </Typography>
);

export const ServicesYouOwn = () => {
  const entityRoute = useRouteRef(entityRouteRef);

  // The page's one answer for what the reader's team owns (DX-307): the
  // Owned components figure above counts the same answer this card lists, so
  // the two cannot disagree. The ownership join is still the catalog's own —
  // the `relations.ownedBy` filter against the caller's ownership refs, the
  // ones the ZITADEL resolver attaches to the token (DX-123) — and with no
  // refs on the identity the catalog is not asked at all (AC-6).
  const { reader, owned } = useHomeSources();
  const loading = reader.loading || owned.loading;
  const error = reader.error ?? owned.error;
  const services = useMemo(
    () =>
      (owned.data ?? [])
        .map(c => toOwnedService(c.entity))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [owned.data],
  );

  // The owning team by its name — a team from the GitHub organisation
  // (DX-202) carries it in its profile, which the presentation API reads. Held
  // in the row rather than rendered, so the table's search and sort go by the
  // name it shows, not by the reference behind it.
  const ownerRefs = useMemo(
    () => [...new Set(services.flatMap(s => (s.owner && s.ownerRef) || []))],
    [services],
  );
  const ownerNames = useEntityNames(ownerRefs);
  const rows = useMemo(() => {
    const nameOf = new Map(ownerRefs.map((ref, i) => [ref, ownerNames[i]]));
    return services.map(s => {
      const name = s.ownerRef ? nameOf.get(s.ownerRef) : undefined;
      return name ? { ...s, ownerLabel: name } : s;
    });
  }, [services, ownerRefs, ownerNames]);

  const columns: TableColumn<OwnedService>[] = [
    {
      title: 'Component',
      field: 'title',
      render: row => (
        <Link
          to={entityRoute({
            kind: row.kind,
            namespace: row.namespace,
            name: row.name,
          })}
        >
          {row.title}
        </Link>
      ),
    },
    {
      // Free: the card already asks the catalog for `spec.type` and already
      // parses it — it was simply never shown. It answers the same question
      // the navigation splits the catalog by, so the reader meets one
      // vocabulary rather than two.
      title: 'Type',
      field: 'type',
      render: row =>
        row.type ? (
          <Typography variant="body2" component="span">
            {row.type}
          </Typography>
        ) : (
          placeholder
        ),
    },
    {
      title: 'Owner',
      field: 'ownerLabel',
      render: row =>
        row.owner ? (
          <Link
            to={entityRoute({
              kind: row.owner.kind,
              namespace: row.owner.namespace,
              name: row.owner.name,
            })}
          >
            {row.ownerLabel}
          </Link>
        ) : (
          // No owner, or one the catalog cannot resolve: show the label as
          // plain text rather than linking somewhere that does not exist.
          <Typography variant="body2" component="span">
            {row.ownerLabel}
          </Typography>
        ),
    },
    {
      title: 'Lifecycle',
      field: 'lifecycle',
      render: row =>
        row.lifecycle ? (
          <Chip
            label={row.lifecycle}
            size="small"
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${lifecycleColor(row.lifecycle)}`,
              color: lifecycleColor(row.lifecycle),
            }}
          />
        ) : (
          placeholder
        ),
    },
  ];

  if (loading) {
    return (
      <InfoCard title="Components you own">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="Components you own">
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  // A reader in no team who owns nothing by their own user gets no card: an
  // empty table would say "your team owns nothing" about a team that is not
  // set, and the home page's team card says what the state is instead
  // (DX-546; specs/services-you-own.md AC-4, home-page.md AC-29). One who
  // owns components by their user is listed like anyone else.
  if (reader.data && !reader.data.inTeam && rows.length === 0) {
    return null;
  }

  return (
    <Table<OwnedService>
      title={`Components you own (${rows.length})`}
      columns={columns}
      data={rows}
      options={{
        search: rows.length > 5,
        paging: rows.length > 10,
        pageSize: 10,
        padding: 'dense',
      }}
      emptyContent={
        <Box p={2}>
          <Typography variant="body2" color="textSecondary">
            No components are owned by your team yet. A component is yours when
            its catalog entry names one of your GitHub teams in the revenexx
            organisation as its owner.
          </Typography>
        </Box>
      }
    />
  );
};
